/*********************************************************
 * Copyright (C) 2016-2020 VMware, Inc. All rights reserved.
 *********************************************************/
/**
 * @fileoverview devicemessagemanager.js -- DeviceMessageManager
 * Class contains the logic for device related messages, mainly works
 * as a interval control since device can't be operated too frequently,
 * and no extra cancel logic are added since driver in the agent should
 * do this.
 *
 * And we can't process start right after stop, or stop right after start,
 * since the browser init is async and some will cause unexcepted errors.
 * and the 600ms is exprimential interval value to safely process actions
 *
 * Although adding minial processing interval for all messages are more safer,
 * and easy to implement, it look not needed and will decrease the user
 * experience somehow, so not use that design.
 */

import RTAV from './logservice';

/**
 * Class contains the logic of what message need to wait
 * @type {function}
 */
export default function MessageWaitManager() {
   let previousActions,
      actionQueue,
      actionWaitMap,
      defaultWaitTime,
      maxRecordDuration,
      waitTimer,
      //functions
      getNow,
      init,
      reset,
      setMaxRecordDuration,
      updateBufferedActions,
      getWaitTimeForAction,
      getWaitTime,
      getDefaultActionWaitTime,
      processQueue,
      canProcess,
      onProcess,
      onWait;

   getNow = function() {
      return (new Date()).getTime();
   };

   reset = function() {
      previousActions = [];
      actionQueue = [];
      waitTimer = null;
   };

   /**
    * it should be >= max(actionWaitMap[k].time, defaultWaitTime), for all valid k
    */
   setMaxRecordDuration = function() {
      let key;

      maxRecordDuration = defaultWaitTime;
      for (key in actionWaitMap) {
         if (actionWaitMap.hasOwnProperty(key)) {
            maxRecordDuration = Math.max(maxRecordDuration, actionWaitMap[key].time);
         }
      }
   };

   init = function() {
      /**
       * @private
       * @type {Object}
       * The format is as: {
       *    previousActionName: {
       *       name: laterActionName,
       *       time: intervalTime
       *    },
       *    ...
       * }
       * and it means if previously we process the message of previousActionName,
       * we must at least wait intervalTime before processing later action of name
       * laterActionName.
       * Choose the same value as the highDelay in the Agent as 600.
       */
      actionWaitMap = {
         "PMsgStart_V": {
            name: "PMsgStop_V",
            time: 600 //ms
         },
         "PMsgStop_V": {
            name: "PMsgStart_V",
            time: 600 //ms
         },
         "PMsgStart_A": {
            name: "PMsgStop_A",
            time: 600 //ms
         },
         "PMsgStop_A": {
            name: "PMsgStart_A",
            time: 600 //ms
         }
      };
      defaultWaitTime = 0; //ms for any messages
      setMaxRecordDuration();
      reset();
   };
   init();

   /**
    * clear outdated record in previousActions, which is sorted by time order
    */
   updateBufferedActions = function() {
      let i,
         oldestRecoderTime = getNow() - maxRecordDuration,
         clipIndex = -1;

      for (i = 0; i < previousActions.length; i++) {
         if (previousActions[i].time < oldestRecoderTime) {
            clipIndex = i;
         } else {
            break;
         }
      }
      if (clipIndex >= 0) {
         previousActions.splice(0, clipIndex + 1);
      }
   };

   /**
    * get wait time for a specified action
    */
   getWaitTimeForAction = function(messageType, currentTime, previousAction) {
      let alreadyWaitTime,
         actionWaitTime,
         waitInfo;

      if (!actionWaitMap.hasOwnProperty(previousAction.name)) {
         return 0;
      }
      waitInfo = actionWaitMap[previousAction.name];
      if (waitInfo.name !== messageType) {
         return 0;
      }
      actionWaitTime = waitInfo.time;
      alreadyWaitTime = currentTime - previousAction.time;
      return Math.max(0, actionWaitTime - alreadyWaitTime);
   };

   /**
    * get wait time for default action, which apply to all messages
    */
   getDefaultActionWaitTime = function(currentTime) {
      let alreadyWaitTime;

      if (previousActions.length === 0) {
         return 0;
      }
      alreadyWaitTime = currentTime - previousActions[previousActions.length - 1].time;

      return Math.max(0, defaultWaitTime - alreadyWaitTime);
   };

   /**
    * get overall wait time
    */
   getWaitTime = function(messageType) {
      let i,
         currentTime = getNow(),
         waitTime = getDefaultActionWaitTime(currentTime);

      for (i = 0; i < previousActions.length; i++) {
         waitTime = Math.max(waitTime, getWaitTimeForAction(messageType, currentTime, previousActions[i]));
      }
      return waitTime;
   };

   processQueue = function() {
      let nextAction;
      if (actionQueue.length === 0) {
         return;
      }
      nextAction = actionQueue[0];
      if (waitTimer !== null) {
         RTAV.logService.trace("there is a message waiting, wait that to be processed first");
         return;
      }

      RTAV.logService.trace("start timer for " + nextAction.messageType);
      waitTimer = setTimeout(function() {
         actionQueue.shift();
         waitTimer = null;
         onProcess(nextAction.messageType);
         nextAction.processingFunction();
         processQueue();
      }, getWaitTime(nextAction.messageType));
   };

   canProcess = function(messageType) {
      return getWaitTime(messageType) === 0 && actionQueue.length === 0;
   };
   onProcess = function(messageType) {
      RTAV.logService.debug("start executing message " + messageType);
      updateBufferedActions();
      previousActions.push({
         name: messageType,
         time: getNow()
      });
   };
   onWait = function(messageType, processingFunction) {
      actionQueue.push({
         messageType: messageType,
         processingFunction: processingFunction
      });
      processQueue();
   };
   return {
      /**
       * This function will run the processingFunction as the order they passed
       * in, and will keep them apart from each other to leave enough interval
       * as defined in the actionWaitMap
       * @param {string} messageType The action Name
       * @param {funciton} processingFunction The function for the action
       */
      process: function(messageType, processingFunction) {
         if (canProcess(messageType)) {
            onProcess(messageType);
            processingFunction();
         } else {
            onWait(messageType, processingFunction);
            RTAV.logService.debug("waiting for message " + messageType);
         }
      },
      /**
       * This is for UT only, and should not be used by program.
       * @type {Object}
       */
      APIforUT: {
         getPreviousActions: function() {
            return previousActions;
         },
         getActionQueue: function() {
            return actionQueue;
         },
         getMaxRecordDuration: function() {
            return maxRecordDuration;
         },
         getWaitTimer: function() {
            return waitTimer;
         }
      }
   };
}

